import { useQuery } from '@tanstack/react-query';
import { clientApi } from '@/lib/api/client';

export interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Falcon's `permissions.role` (see `PermissionsService.getUserPermissions`).
 *
 * `isSuperAdmin` is the GLOBAL role (`users.roles.__global_roles__` contains
 * `superAdmin`) and is the only flag that grants the admin control panel.
 * `isCompanyAdmin` is company-scoped and does NOT — the control-panel routes
 * answer 403 for it.
 */
export interface ProfileRole {
  type?: string;
  isCompanyAdmin?: boolean;
  isSuperAdmin?: boolean;
  isFreeVersion?: boolean;
}

export interface ProfileCompany {
  id: string;
  name?: string;
}

export interface ProfileResponse {
  user: CurrentUser;
  role?: ProfileRole;
  access?: Record<string, unknown>;
  /** Present once the backend surfaces the current user's company. */
  company?: ProfileCompany;
}

/** True for a global superAdmin — the audience of `/admin/control-panel`. */
export function isSuperAdmin(profile: Pick<ProfileResponse, 'role'> | undefined): boolean {
  return profile?.role?.isSuperAdmin === true;
}

/** True for a company on the free (unpaid) plan — gates the ChangePlan Pro upsell. */
export function isFreeVersion(profile: Pick<ProfileResponse, 'role'> | undefined): boolean {
  return profile?.role?.isFreeVersion === true;
}

export function getProfile(): Promise<ProfileResponse> {
  return clientApi<ProfileResponse>('profile');
}

/** Current authenticated user — mirrors the Angular ProfileService.getProfile(). */
export function useCurrentUser({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['profile'],
    queryFn: getProfile,
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });
}

/** `useCurrentUser` narrowed to just the free-plan flag, for gating a single control. */
export function useFreeVersion(): boolean {
  const { data } = useCurrentUser();
  return isFreeVersion(data);
}

/** `POST profile/pro-inquiry` (v1: `sendChangePlanProEmail`) — notifies sales. */
export function sendProInquiry(): Promise<void> {
  return clientApi<void>('profile/pro-inquiry', { method: 'POST' });
}
